// src/pages/markets/api/usePositionTrades.ts
//
// Lazy per-position trade fetch. Used by PositionTradesPanel when a row is
// expanded. Fetches one side's fills via the existing account-trades endpoint
// with the new `outcome_index` filter, maps to the shared MarketTrade shape,
// and caches per (address, conditionId, outcomeIndex) so re-expanding is free.

import { useState, useEffect, useRef } from 'react';
import { apiGet } from './apiClient';
import { indexerTradeToMarketTrade } from './sidebarTradeTypes';
import type { IndexerTradeItem } from './sidebarTradeTypes';
import type { MarketTrade } from './useMarketTrades';

interface AccountTradesResponse {
    trades: IndexerTradeItem[];
    trade_count: number;
    has_more: boolean;
}

export interface UsePositionTradesResult {
    trades: MarketTrade[];
    loading: boolean;
    error: string | null;
}

// Module-level cache shared across all hook instances (and remounts), so
// collapsing/re-expanding or re-rendering the table never refetches.
const cache = new Map<string, MarketTrade[]>();

function cacheKey(
    address: string,
    conditionId: string,
    outcomeIndex: number | undefined
): string {
    // outcomeIndex can be 0, so ?? rather than || to keep the "no filter" key
    // distinct from the YES-side (0) key.
    return `${address.toLowerCase()}|${conditionId}|${outcomeIndex ?? 'all'}`;
}

export function usePositionTrades(
    address: string | undefined,
    conditionId: string | undefined,
    // 0 = YES, 1 = NO. Optional — omit to fetch both sides for the market.
    outcomeIndex: number | undefined,
    // Set false to defer the fetch until the row is actually expanded.
    enabled = true
): UsePositionTradesResult {
    const [trades, setTrades] = useState<MarketTrade[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const callIdRef = useRef(0);
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (!enabled) return;
        if (!address || !conditionId) return;

        const key = cacheKey(address, conditionId, outcomeIndex);
        const cached = cache.get(key);
        if (cached) {
            setTrades(cached);
            setLoading(false);
            setError(null);
            return;
        }

        const callId = ++callIdRef.current;
        setLoading(true);
        setError(null);

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        const addr = address.replace(/^polymarket:/, '');
        const params = new URLSearchParams({
            markets: conditionId,
            limit: '500',
        });
        if (outcomeIndex != null)
            params.set('outcome_index', String(outcomeIndex));

        apiGet<AccountTradesResponse>(
            `/api/trades/account/${encodeURIComponent(addr)}?${params}`,
            controller.signal
        )
            .then(res => {
                if (callId !== callIdRef.current) return;
                const mapped = res.trades.map(indexerTradeToMarketTrade);
                cache.set(key, mapped);
                setTrades(mapped);
            })
            .catch(err => {
                if (err?.name === 'AbortError' || callId !== callIdRef.current)
                    return;
                console.error('position-trades fetch failed:', err);
                setError(err instanceof Error ? err.message : String(err));
                setTrades([]);
            })
            .finally(() => {
                if (callId !== callIdRef.current) return;
                setLoading(false);
            });
    }, [address, conditionId, outcomeIndex, enabled]);

    useEffect(() => () => abortRef.current?.abort(), []);

    return { trades, loading, error };
}
